import React, { useEffect, useMemo, useState } from "react";
import {
  CompetitionGroup,
  CompetitionStage,
  CompetitionStageWritePayload,
  GroupStanding,
  ID,
  Match,
  TournamentParticipant,
  KNOCKOUT_LEVEL,
  SEEDING_METHOD,
  MATCH_FORMAT,
  STAGE_TYPE,
  KnockoutLevel,
} from "../../../types";
import { Button } from "../Button";
import { CompetitionStageGroups } from "./stage-panels/CompetitionStageGroups";
import { CompetitionStageKnockout } from "./stage-panels/CompetitionStageKnockout";
import { CompetitionStageNotGenerated } from "./stage-panels/CompetitionStageNotGenerated";
import { StageEditModal } from "./StageEditModal";
import { AddPlacementMatchModal } from "./AddPlacementMatchModal";
import { useTournamentEvent } from "./TournamentEventContext";

interface CompetitionStagePanelProps {
  stage: CompetitionStage;
  stages: CompetitionStage[];
  groups: CompetitionGroup[];
  matches: Match[];
  standings: GroupStanding[];
  participants: TournamentParticipant[];
  generating: boolean;
  generatingKnockout: boolean;
  updatingGroupId: ID | null;
  onUpdateStage: (payload: CompetitionStageWritePayload) => Promise<void>;
  onGenerate: (participantIds: ID[], matchDurationMinutes: number) => Promise<void>;
  onGenerateKnockout: () => Promise<void>;
  onUpdateGroupParticipants: (groupId: ID, participantIds: ID[]) => Promise<void>;
  onDelete: () => Promise<void>;
}

export const CompetitionStagePanel: React.FC<CompetitionStagePanelProps> = ({
  stage,
  stages,
  groups,
  matches,
  standings,
  participants,
  generating,
  generatingKnockout,
  updatingGroupId,
  onUpdateStage,
  onGenerate,
  onGenerateKnockout,
  onUpdateGroupParticipants,
  onDelete,
}) => {
  const { canManage, enableCalendar } = useTournamentEvent();
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingMatch, setIsAddingMatch] = useState(false);
  const [matchDurationMinutes, setMatchDurationMinutes] = useState(60);
  // Le finali di piazzamento (1°-2°, 3°-4°, …) si aggiungono a mano solo in uno
  // stage knockout di tipo "final".
  const isPlacementFinalStage = stage.stage_type === "knockout" && stage.knockout_stage_type === "final";
  const participantsById = useMemo(() => new Map(participants.map((p) => [p.id, p])), [participants]);
  const stageGroups = useMemo(() => groups.filter((g) => g.stage === stage.id), [groups, stage.id]);
  const stageMatches = useMemo(() => matches.filter((m) => m.stage === stage.id), [matches, stage.id]);
  const generatedKnockoutStage = useMemo(
    () => stages.find((item) => item.source_stage === stage.id && item.stage_type === "knockout") ?? null,
    [stages, stage.id]
  );
  const standingsByGroup = useMemo(() => {
    const map = new Map<ID, GroupStanding[]>();
    standings.forEach((s) => {
      const list = map.get(s.group) || [];
      list.push(s);
      map.set(s.group, list);
    });
    return map;
  }, [standings]);

  const [selectedParticipantIds, setSelectedParticipantIds] = useState<ID[]>([]);

  const hasGeneratedData = stageMatches.length > 0 || stageGroups.length > 0;
  const selectedParticipantsSet = useMemo(
    () => new Set(selectedParticipantIds),
    [selectedParticipantIds]
  );

  useEffect(() => {
    const initialSelection =
      stage.participants && stage.participants.length > 0
        ? stage.participants
        : participants.map((p) => p.id);
    setSelectedParticipantIds(initialSelection);
  }, [stage.id, stage.participants, participants]);


  const getInfoGroups = (stage: CompetitionStage) => {
    let gironi = `Gironi: ${stage.num_groups} (${SEEDING_METHOD[stage.seeding_method!]}) ${MATCH_FORMAT[stage.match_format!]}`;
    let qualifiers = '';
    if (stage.ranking_start_position) {
      qualifiers = ` - Valido per posizioni ${stage.ranking_start_position}-${stage.participants?.length + stage.ranking_start_position - 1}`;
    }
    else {
      let w = stage.wildcards && stage.wildcards > 0 ? `+ ${stage.wildcards}W` : '';
      let lbl_top = stage.qualifiers_per_group === 1 ? 'passa il primo classificato' : `passano i primi ${stage.qualifiers_per_group} classificati`;
      qualifiers = ` - ${lbl_top} ${w}`;
    }
    return gironi + qualifiers;
  };

  const getInfoKnockout = (stage: CompetitionStage) => {
    let format = STAGE_TYPE.knockout + ': ';
    let levels = Object.keys(KNOCKOUT_LEVEL);
    let i_start = levels.indexOf(stage.knockout_stage_type as KnockoutLevel);
    for (let i = i_start; i < levels.length; i++) {
      format += `${KNOCKOUT_LEVEL[levels[i] as KnockoutLevel]}, `;
    }
    format = format.slice(0, -2);
    if (stage.ranking_start_position) {
      format += ` - Valido per posizioni ${stage.ranking_start_position}-${stage.participants?.length + stage.ranking_start_position - 1}`;
    }

    return format;
  };

  const assignableParticipants = useMemo(() => {
    if (!selectedParticipantIds.length) return [];
    return participants.filter((p) => selectedParticipantsSet.has(p.id));
  }, [participants, selectedParticipantsSet, selectedParticipantIds.length]);
  

  const handleSelectionChange = (nextSelection: ID[]) => {
    setSelectedParticipantIds(nextSelection);
  };

  const handleGenerate = async () => {
    if (!canManage || generating) return;
    if (selectedParticipantIds.length < 2) {
      alert("Seleziona almeno 2 partecipanti.");
      return;
    }
    if (enableCalendar && (!Number.isInteger(matchDurationMinutes) || matchDurationMinutes < 1)) {
      alert("Inserisci una durata valida in minuti.");
      return;
    }
    if (stageMatches.length > 0) {
      const confirmed = window.confirm(
        "Questo stage ha gia dei match generati. Vuoi rigenerarlo?"
      );
      if (!confirmed) return;
    }
    await onGenerate(selectedParticipantIds, matchDurationMinutes);
  };

  const handleDelete = async () => {
    if (!canManage) return;
    const confirmed = window.confirm("Vuoi eliminare questo stage?");
    if (!confirmed) return;
    await onDelete();
  };

  const handleOpenEdit = () => {
    if (!canManage) return;
    setIsEditing(true);
  };

  return (
    <>
      <div className="bg-white border border-gray-300 rounded shadow-sm p-5 my-6">
        <div className="pb-2">
          <div className="flex justify-between items-center gap-2">
            <h2 className="text-lg font-semibold">{stage.name}</h2>
            {canManage && (
              <Button size="sm" variant="warning" onClick={handleOpenEdit}>
                Modifica
              </Button>
            )}
          </div>
        </div>
        <div>
          <p className="text-sm text-gray-600">
            <span>Fase: {stage.order}</span>
          </p>
          <p className="text-sm text-gray-600">
            {stage.stage_type === "groups" && (<i>{getInfoGroups(stage)}</i>)}
            {stage.stage_type === "knockout" && (<i>{getInfoKnockout(stage)}</i>)}
          </p>
        </div>
      </div>

      {!hasGeneratedData && (
        <CompetitionStageNotGenerated
          stageId={stage.id}
          participants={participants}
          selectedParticipantIds={selectedParticipantIds}
          matchDurationMinutes={matchDurationMinutes}
          onMatchDurationMinutesChange={setMatchDurationMinutes}
          onSelectionChange={handleSelectionChange}
        />
      )}

      {hasGeneratedData && stage.stage_type === "groups" && (
        <CompetitionStageGroups
          stage={stage}
          generatedKnockoutStage={generatedKnockoutStage}
          stageGroups={stageGroups}
          stageMatches={stageMatches}
          standingsByGroup={standingsByGroup}
          participants={participants}
          participantsById={participantsById}
          assignableParticipants={assignableParticipants}
          generatingKnockout={generatingKnockout}
          updatingGroupId={updatingGroupId}
          onGenerateKnockout={onGenerateKnockout}
          onUpdateGroupParticipants={onUpdateGroupParticipants}
        />
      )}

      {hasGeneratedData && stage.stage_type === "knockout" && (
        <CompetitionStageKnockout
          stage={stage}
          stageMatches={stageMatches}
          participantsById={participantsById}
          assignableParticipants={assignableParticipants}
        />
      )}

      {canManage && (
        <div className="flex justify-between gap-2 mt-4">
          <Button variant="destructive" onClick={handleDelete}>
            Elimina stage
          </Button>
          <div className="flex gap-2">
            {isPlacementFinalStage && (
              <Button variant="success" onClick={() => setIsAddingMatch(true)}>
                Aggiungi match
              </Button>
            )}
            <Button onClick={handleGenerate} disabled={generating || selectedParticipantIds.length < 2}>
              {generating ? "Generazione..." : "Genera stage"}
            </Button>
          </div>
        </div>
      )}

      {isEditing && (
        <StageEditModal stage={stage} onClose={() => setIsEditing(false)} onSave={onUpdateStage} />
      )}

      {isAddingMatch && (
        <AddPlacementMatchModal
          stage={stage}
          participants={assignableParticipants}
          existingMatches={stageMatches}
          onClose={() => setIsAddingMatch(false)}
        />
      )}
    </>
  );
};
